import React, { useState, useEffect } from 'react';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import TabNavigation from '../../components/ui/TabNavigation';
import DailyReadingCard from './components/DailyReadingCard';
import GoalHorizonSection from './components/GoalHorizonSection';
import Core4Section from './components/Core4Section';
import YesterdayCompletionSection from './components/YesterdayCompletionSection';
import QuickNavigationSection from './components/QuickNavigationSection';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { goalsService } from '../../services/goalsService';
import { core4Service } from '../../services/core4Service';
import { readingsService } from '../../services/readingsService';
import { analyticsService } from '../../services/analyticsService';

const TodayPage = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState(1);
  const [selectedDay, setSelectedDay] = useState(1);
  
  // Goal management state - now empty arrays instead of mock data
  const [expandedSections, setExpandedSections] = useState({
    fiveYear: true,
    ninetyDay: true,
    daily: true
  });

  const [goals, setGoals] = useState({
    fiveYear: [],
    ninetyDay: [],
    daily: []
  });

  // CORE4 state - now empty instead of mock data
  const [core4Goals, setCore4Goals] = useState({
    faith: '',
    family: '',
    finances: '',
    fitness: ''
  });

  const [yesterdayCompletions, setYesterdayCompletions] = useState({
    faith: false,
    family: false,
    finances: false,
    fitness: false
  });

  const [selectedReading, setSelectedReading] = useState(null);

  // Calculate current curriculum day based on user's start date
  const calculateCurrentDay = (startDate) => {
    if (!startDate) return 1;
    
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate);
    start?.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(today - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Cycle through 42-day curriculum using modulo
    // Day 0 becomes day 42, day 1 stays day 1, day 42 stays day 42, day 43 becomes day 1, etc.
    const cycleDay = diffDays % 42;
    return cycleDay === 0 ? 42 : cycleDay;
  };

  // Load initial data
  useEffect(() => {
    if (user?.id && userProfile) {
      loadTodayData();
    }
  }, [user?.id, userProfile]);

  const loadTodayData = async () => {
    if (!user?.id) return;

    setLoading(true);
    
    try {
      // Calculate current day based on user's program start date
      const day = calculateCurrentDay(userProfile?.program_start_date);
      setCurrentDay(day);
      setSelectedDay(day);

      // Load user goals for all horizons
      await Promise.all([
        loadGoalsByHorizon('five_year'),
        loadGoalsByHorizon('ninety_day'), 
        loadGoalsByHorizon('daily')
      ]);

      // Load today's CORE4 entries
      await loadTodaysCore4();
      
      // Load yesterday's CORE4 completions
      await loadYesterdaysCore4();

      // Load today's reading
      await loadTodaysReading(day);

    } catch (error) {
      console.error('Error loading today data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoalsByHorizon = async (horizon) => {
    const { goals: fetchedGoals, error } = await goalsService?.getUserGoals(user?.id, horizon);
    
    if (error) {
      console.error(`Error loading ${horizon} goals:`, error);
      return;
    }

    // Map database format to component format
    const mappedGoals = fetchedGoals?.map(goal => ({
      id: goal?.id,
      text: goal?.goal_text,
      completed: goal?.is_completed
    })) || [];

    setGoals(prev => ({
      ...prev,
      [horizon === 'five_year' ? 'fiveYear' : horizon === 'ninety_day' ? 'ninetyDay' : 'daily']: mappedGoals
    }));
  };

  const loadTodaysCore4 = async () => {
    const { entries, error } = await core4Service?.getCore4Entries(user?.id);
    
    if (error) {
      console.error('Error loading CORE4 entries:', error);
      return;
    }

    // Map entries to state format
    const core4Data = {
      faith: '',
      family: '',
      finances: '',
      fitness: ''
    };

    entries?.forEach(entry => {
      if (core4Data?.hasOwnProperty(entry?.area)) {
        core4Data[entry.area] = entry?.goal_description || '';
      }
    });

    setCore4Goals(core4Data);
  };

  const loadYesterdaysCore4 = async () => {
    const yesterday = new Date();
    yesterday?.setDate(yesterday?.getDate() - 1);
    const yesterdayStr = yesterday?.toISOString()?.split('T')?.[0];

    const { entries, error } = await core4Service?.getCore4Entries(user?.id, yesterdayStr);
    
    if (error) {
      console.error('Error loading yesterday CORE4:', error);
      return;
    }

    // Map entries to completion status
    const completions = {
      faith: false,
      family: false,
      finances: false,
      fitness: false
    };

    entries?.forEach(entry => {
      if (completions?.hasOwnProperty(entry?.area)) {
        completions[entry.area] = entry?.status === 'completed';
      }
    });

    setYesterdayCompletions(completions);
  };

  const loadTodaysReading = async (day) => {
    const { reading, error } = await readingsService?.getReadingByDay(day);
    
    if (error) {
      console.error('Error loading reading:', error);
      setSelectedReading(null);
      return;
    }

    setSelectedReading(reading);
  };

  // Day navigation functions
  const navigateToPreviousDay = () => {
    if (selectedDay > 1) {
      const newDay = selectedDay - 1;
      setSelectedDay(newDay);
      loadTodaysReading(newDay);
    }
  };

  const navigateToNextDay = () => {
    const newDay = selectedDay + 1;
    setSelectedDay(newDay);
    loadTodaysReading(newDay);
  };

  const navigateToDay = (day) => {
    setSelectedDay(day);
    loadTodaysReading(day);
  };

  const resetToCurrentDay = () => {
    setSelectedDay(currentDay);
    loadTodaysReading(currentDay);
  };

  // Goal management functions - now use Supabase
  const toggleGoalCompletion = async (horizon, goalId, completed) => {
    const { error } = await goalsService?.toggleGoalCompletion(goalId, completed);
    
    if (error) {
      console.error('Error toggling goal:', error);
      return;
    }

    // Update local state
    setGoals(prev => ({
      ...prev,
      [horizon]: prev?.[horizon]?.map(goal =>
        goal?.id === goalId ? { ...goal, completed } : goal
      )
    }));

    // Update daily activity tracking
    updateDailyActivity();
  };

  const addGoal = async (horizon, text) => {
    const horizonMapping = {
      'fiveYear': 'five_year',
      'ninetyDay': 'ninety_day', 
      'daily': 'daily'
    };

    const { goal, error } = await goalsService?.createGoal(user?.id, {
      horizon: horizonMapping?.[horizon],
      text,
      orderIndex: goals?.[horizon]?.length || 0
    });

    if (error) {
      console.error('Error creating goal:', error);
      return;
    }

    // Add to local state
    const newGoal = {
      id: goal?.id,
      text: goal?.goal_text,
      completed: goal?.is_completed
    };

    setGoals(prev => ({
      ...prev,
      [horizon]: [...prev?.[horizon], newGoal]
    }));
  };

  const removeGoal = async (horizon, goalId) => {
    const { error } = await goalsService?.deleteGoal(goalId);
    
    if (error) {
      console.error('Error deleting goal:', error);
      return;
    }

    // Remove from local state
    setGoals(prev => ({
      ...prev,
      [horizon]: prev?.[horizon]?.filter(goal => goal?.id !== goalId)
    }));

    updateDailyActivity();
  };

  const editGoal = async (horizon, goalId, newText) => {
    const { error } = await goalsService?.updateGoal(goalId, { text: newText });
    
    if (error) {
      console.error('Error updating goal:', error);
      return;
    }

    // Update local state
    setGoals(prev => ({
      ...prev,
      [horizon]: prev?.[horizon]?.map(goal =>
        goal?.id === goalId ? { ...goal, text: newText } : goal
      )
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleCore4Change = async (area, value) => {
    // Update local state immediately
    setCore4Goals(prev => ({
      ...prev,
      [area]: value
    }));

    // Update database
    const { error } = await core4Service?.upsertCore4Entry(user?.id, {
      area,
      goalDescription: value,
      status: 'pending'
    });

    if (error) {
      console.error('Error updating CORE4:', error);
    }
  };

  const handleYesterdayToggle = async (area, completed) => {
    // Update local state
    setYesterdayCompletions(prev => ({
      ...prev,
      [area]: completed
    }));

    // Update yesterday's entry in database
    const yesterday = new Date();
    yesterday?.setDate(yesterday?.getDate() - 1);
    const yesterdayStr = yesterday?.toISOString()?.split('T')?.[0];

    const { error } = await core4Service?.toggleCore4Completion(
      user?.id, 
      area, 
      yesterdayStr, 
      completed
    );

    if (error) {
      console.error('Error updating yesterday CORE4:', error);
    } else {
      updateDailyActivity();
    }
  };

  const updateDailyActivity = async () => {
    if (!user?.id) return;

    // Calculate completion counts
    const completedGoals = Object.values(goals)?.flat()?.filter(goal => goal?.completed)?.length;
    const completedCore4 = Object.values(yesterdayCompletions)?.filter(Boolean)?.length;
    const engagementScore = Math.min(((completedGoals + completedCore4) / 8) * 100, 100);

    await analyticsService?.updateDailyActivity(user?.id, {
      goalsCompleted: completedGoals,
      core4Completed: completedCore4,
      readingCompleted: selectedReading !== null,
      engagementScore: Math.round(engagementScore)
    });
  };

  const isPostCurriculum = selectedDay > 42;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading your daily goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader 
        userRole={userProfile?.role}
        userName={userProfile?.full_name}
      />
      <main className="container mx-auto px-4 py-6 pb-20 lg:pb-6">
        {/* Conditional rendering: Show reading card only for curriculum days (1-42) */}
        {!isPostCurriculum && selectedReading && (
          <DailyReadingCard 
            currentDay={currentDay}
            selectedDay={selectedDay}
            reading={selectedReading}
            onPreviousDay={navigateToPreviousDay}
            onNextDay={navigateToNextDay}
            onDaySelect={navigateToDay}
            onResetToToday={resetToCurrentDay}
            isPostCurriculum={isPostCurriculum}
          />
        )}

        {/* Post-curriculum navigation header - shows only for days beyond 42 */}
        {isPostCurriculum && (
          <div className="bg-card border border-border rounded-lg shadow-gentle p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                  <Icon name="Calendar" size={20} color="white" />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={navigateToPreviousDay}
                    disabled={selectedDay <= 1}
                    className={`p-1 rounded transition-colors duration-200 ${
                      selectedDay > 1 
                        ? 'text-muted-foreground hover:text-foreground hover:bg-muted' 
                        : 'text-muted-foreground/50 cursor-not-allowed'
                    }`}
                    title="Previous day"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  
                  <div className="text-center">
                    <h2 className="text-xl font-heading font-semibold text-foreground">
                      Day {selectedDay}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {selectedDay === currentDay ? 'Today' : 'Daily Goals'}
                    </p>
                  </div>
                  
                  <button
                    onClick={navigateToNextDay}
                    className="p-1 rounded transition-colors duration-200 text-muted-foreground hover:text-foreground hover:bg-muted"
                    title="Next day"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                </div>
              </div>
              
              {selectedDay !== currentDay && (
                <button
                  onClick={resetToCurrentDay}
                  className="text-sm text-primary hover:text-primary/80 underline transition-colors duration-200"
                >
                  Return to Today
                </button>
              )}
            </div>
          </div>
        )}

        {/* CORE4 Section */}
        <Core4Section
          core4Goals={core4Goals}
          onCore4Change={handleCore4Change}
        />

        {/* Yesterday's Completion Section */}
        <YesterdayCompletionSection
          yesterdayCompletions={yesterdayCompletions}
          onYesterdayToggle={handleYesterdayToggle}
        />

        {/* Goal Horizons */}
        <div className="space-y-4 mb-6">
          <GoalHorizonSection
            title="5-Year"
            icon="Target"
            goals={goals?.fiveYear}
            onGoalToggle={(goalId, completed) => toggleGoalCompletion('fiveYear', goalId, completed)}
            onGoalAdd={(text) => addGoal('fiveYear', text)}
            onGoalRemove={(goalId) => removeGoal('fiveYear', goalId)}
            onGoalEdit={(goalId, text) => editGoal('fiveYear', goalId, text)}
            isExpanded={expandedSections?.fiveYear}
            onToggleExpanded={() => toggleSection('fiveYear')}
          />

          <GoalHorizonSection
            title="90-Day"
            icon="Calendar"
            goals={goals?.ninetyDay}
            onGoalToggle={(goalId, completed) => toggleGoalCompletion('ninetyDay', goalId, completed)}
            onGoalAdd={(text) => addGoal('ninetyDay', text)}
            onGoalRemove={(goalId) => removeGoal('ninetyDay', goalId)}
            onGoalEdit={(goalId, text) => editGoal('ninetyDay', goalId, text)}
            isExpanded={expandedSections?.ninetyDay}
            onToggleExpanded={() => toggleSection('ninetyDay')}
          />

          <GoalHorizonSection
            title="Daily"
            icon="Sun"
            goals={goals?.daily}
            onGoalToggle={(goalId, completed) => toggleGoalCompletion('daily', goalId, completed)}
            onGoalAdd={(text) => addGoal('daily', text)}
            onGoalRemove={(goalId) => removeGoal('daily', goalId)}
            onGoalEdit={(goalId, text) => editGoal('daily', goalId, text)}
            isExpanded={expandedSections?.daily}
            onToggleExpanded={() => toggleSection('daily')}
          />
        </div>

        {/* Quick Navigation */}
        <QuickNavigationSection />
      </main>
      
      {/* Mobile Bottom Navigation */}
      <TabNavigation />
    </div>
  );
};

export default TodayPage;